import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Check as CheckIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { dateFormat } from '../../utils';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as g04GeneralDocuActions from '../../store/g04GeneralDocu';
import * as g04docuGCertificationActions from '../../store/g04docuGCertification';
import { joinClassificationParts } from '../GTestComparison/leadFieldUtils';
import SubMatFolderIcon from './SubMatFolderIcon';
import { formatSubMatEndDate, formatSubMatExpiryStatusText, getSubMatExpirySortValue, getSubMatExpiryStatusSortValue, getSubMatExpiryStatusTextColor } from './SubMatFolderExpiryStatus';
import { bindLongPress } from './subMatLongPress';
import {
  ColumnDataCell,
  ColumnHeaderCell,
  ColumnRow,
  useColumnWidths,
} from './SubMatFolderColumnLayout';

/** 버전 | 이름 | 수정한 날짜 | 만료일 | 상태 | 크기 | 시험성과대비표 | 사용중 */
const HISTORY_COL_MIN = [40, 90, 80, 80, 56, 48, 72, 48];
const HISTORY_COL_DEFAULT = [48, 140, 120, 96, 80, 64, 100, 56];

/** 윈도우 탐색기형: 날짜 첫 클릭=최신순, 버전 첫 클릭=높은 버전 */
const HISTORY_SORT_FIRST_DIR = {
  version: 'desc',
  name: 'asc',
  modified: 'desc',
  expiry: 'asc',
  expiryStatus: 'asc',
  size: 'asc',
};

const compareHistoryFiles = (a, b, key, dir) => {
  const mul = dir === 'asc' ? 1 : -1;
  let c = 0;
  switch (key) {
    case 'version':
      c = (Number(a.versionNo) || 0) - (Number(b.versionNo) || 0);
      break;
    case 'name':
      c = (a.fileName || '').localeCompare(b.fileName || '', 'ko', {
        numeric: true,
        sensitivity: 'base',
      });
      break;
    case 'modified': {
      const ta = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
      const tb = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
      c = ta - tb;
      break;
    }
    case 'expiry':
      c = getSubMatExpirySortValue(a) - getSubMatExpirySortValue(b);
      break;
    case 'expiryStatus':
      c = getSubMatExpiryStatusSortValue(a) - getSubMatExpiryStatusSortValue(b);
      break;
    case 'size':
      c = (Number(a.size) || 0) - (Number(b.size) || 0);
      break;
    default:
      c = 0;
  }
  if (c === 0) {
    c = (Number(b.versionNo) || 0) - (Number(a.versionNo) || 0);
    return c;
  }
  return c * mul;
};

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === '') return '';
  const n = Number(bytes);
  if (Number.isNaN(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const CheckEmptyIcon = () => (
  <CheckIcon
    sx={{
      fontSize: 18,
      color: 'action.disabled',
      opacity: 0.35,
    }}
  />
);

const CheckMarkIcon = () => (
  <CheckIcon
    sx={{
      fontSize: 18,
      color: 'success.main',
      stroke: 'currentColor',
      strokeWidth: 0.5,
    }}
  />
);

const historyGridTemplate = (widths) => (
  HISTORY_COL_DEFAULT.map((def, i) => `${widths[i] ?? def}px`).join(' ')
);

const HistoryHeader = ({ widths, startResize, sortKey, sortDir, onSort }) => {
  const sortProps = (key) => ({
    sortable: true,
    sortActive: sortKey === key,
    sortDir,
    onSort: () => onSort?.(key),
  });
  return (
    <ColumnRow header gridTemplateColumns={historyGridTemplate(widths)} sx={{ mb: 0.25 }}>
      <ColumnHeaderCell
        label="버전"
        align="right"
        resizable
        onResizeStart={(x) => startResize(0, x)}
        {...sortProps('version')}
      />
      <ColumnHeaderCell
        label="이름"
        resizable
        onResizeStart={(x) => startResize(1, x)}
        {...sortProps('name')}
      />
      <ColumnHeaderCell
        label="수정한 날짜"
        resizable
        onResizeStart={(x) => startResize(2, x)}
        {...sortProps('modified')}
      />
      <ColumnHeaderCell
        label="만료일"
        resizable
        onResizeStart={(x) => startResize(3, x)}
        {...sortProps('expiry')}
      />
      <ColumnHeaderCell
        label="상태"
        resizable
        onResizeStart={(x) => startResize(4, x)}
        {...sortProps('expiryStatus')}
      />
      <ColumnHeaderCell
        label="크기"
        align="right"
        resizable
        onResizeStart={(x) => startResize(5, x)}
        {...sortProps('size')}
      />
      <ColumnHeaderCell
        label="시험성과대비표"
        resizable
        onResizeStart={(x) => startResize(6, x)}
      />
      <ColumnHeaderCell
        label="사용중"
        resizable
        isLast
        onResizeStart={(x) => startResize(7, x)}
      />
    </ColumnRow>
  );
};

const HistoryRow = ({
  widths,
  file,
  folder,
  current = false,
  checkDisabled = false,
  onSelect,
  onToggleCurrent,
  onContextMenu,
  onPreviewFile,
  onError,
}) => {
  const handleOpen = async (e) => {
    e.stopPropagation();
    if (!file.documentPath) return;
    try {
      await onPreviewFile?.(file);
    } catch (err) {
      onError?.(err.message || '파일을 열 수 없습니다.');
    }
  };

  const handlePreviewComparison = async (e) => {
    e.stopPropagation();
    const cmp = file.comparisonFile;
    if (!cmp?.documentPath) return;
    try {
      await onPreviewFile?.(cmp);
    } catch (err) {
      onError?.(err.message || '시험성과대비표를 열 수 없습니다.');
    }
  };

  const isComparison = file.fileRole === 'TEST_COMPARISON';
  const showCurrentCheck = !isComparison;

  return (
    <ColumnRow
      data-submat-ctx=""
      gridTemplateColumns={historyGridTemplate(widths)}
      onClick={() => onSelect?.(file.id)}
      onDoubleClick={handleOpen}
      onContextMenu={(e) => onContextMenu?.(e, { kind: 'file', folder, file })}
      sx={{
        py: 0.5,
        mb: 0.25,
        borderRadius: 0.5,
        cursor: 'pointer',
        bgcolor: 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <ColumnDataCell
        align="right"
        sx={{
          color: current ? 'text.primary' : 'text.disabled',
          fontWeight: current ? 600 : 400,
          fontVariantNumeric: 'tabular-nums',
          fontSize: '0.75rem',
        }}
      >
        {file.versionNo ?? ''}
      </ColumnDataCell>
      <ColumnDataCell
        sx={{
          color: current ? 'text.primary' : 'text.secondary',
          fontWeight: current ? 500 : 400,
          fontSize: '0.875rem',
        }}
        title={file.fileName}
      >
        {file.fileName}
      </ColumnDataCell>
      <ColumnDataCell sx={{ opacity: current ? 1 : 0.85 }}>
        {dateFormat(file.uploadedAt)}
      </ColumnDataCell>
      <ColumnDataCell
        sx={{ fontVariantNumeric: 'tabular-nums', opacity: current ? 1 : 0.85 }}
      >
        {formatSubMatEndDate(file)}
      </ColumnDataCell>
      <ColumnDataCell
        sx={{
          opacity: current ? 1 : 0.85,
          color: getSubMatExpiryStatusTextColor(file),
        }}
      >
        {formatSubMatExpiryStatusText(file)}
      </ColumnDataCell>
      <ColumnDataCell
        align="right"
        sx={{ fontVariantNumeric: 'tabular-nums', opacity: current ? 1 : 0.85 }}
      >
        {formatFileSize(file.size)}
      </ColumnDataCell>
      <ColumnDataCell align="center">
        {file.comparisonFile?.documentPath ? (
          <Button
            size="small"
            variant="text"
            onClick={handlePreviewComparison}
            sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.75rem' }}
          >
            보기
          </Button>
        ) : null}
      </ColumnDataCell>
      <ColumnDataCell align="center">
        {showCurrentCheck ? (
          <Checkbox
            size="small"
            color="success"
            checked={current}
            disabled={checkDisabled}
            icon={<CheckEmptyIcon />}
            checkedIcon={<CheckMarkIcon />}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              if (e.target.checked) onToggleCurrent?.(e, file);
            }}
            inputProps={{ 'aria-label': `${file.fileName} 사용중으로 설정` }}
            sx={{ p: 0.25 }}
          />
        ) : null}
      </ColumnDataCell>
    </ColumnRow>
  );
};

const generalDocNames = new Map();
let generalDocLoad = null;

const loadGeneralDocName = async (id) => {
  if (!id) return '';
  if (generalDocNames.has(id)) return generalDocNames.get(id);
  if (!generalDocLoad) {
    generalDocLoad = g04GeneralDocuActions.selectAllDirect()
      .then((list) => {
        (list || []).forEach((doc) => {
          if (doc?.id) generalDocNames.set(doc.id, doc.name || '');
        });
      })
      .catch(() => {
        generalDocLoad = null;
      });
  }
  await generalDocLoad;
  return generalDocNames.get(id) || '';
};

const certLabels = new Map();

const loadCertLabel = async (id) => {
  if (!id) return '';
  if (certLabels.has(id)) return certLabels.get(id);
  try {
    const cert = await g04docuGCertificationActions.selectByDirect(id);
    const label = [cert?.gstandardNo, cert?.name].filter(Boolean).join(' ');
    certLabels.set(id, label);
    return label;
  } catch {
    return '';
  }
};

const isProcessSpec = (spec) => Boolean(spec?.code || spec?.codeByStandard || spec?.glass);

/** 레거시 사양 표시와 동일. 6|CL+12|A → 6 CL + 12 A, 6|로이유리 → 6 로이유리 */
const formatProductCode = (code) => String(code || '')
  .replaceAll('|', ' ')
  .replaceAll('+', ' + ');

/** 규격 폴더의 성적서 조회 코드. 두께|제품명. 제품명은 부모 제품 폴더에 있다. */
const reportLookupCode = (folder, folders) => {
  const spec = folder?.spec;
  const thickness = spec?.thickness != null ? String(spec.thickness).trim() : '';
  if (!thickness) return '';
  const product = (Array.isArray(folders) ? folders : []).find((item) => item?.id === spec.productFolderId)
    || subMatFolderActions.ancestorNamedProduct(folder, folders);
  const productName = String(product?.spec?.productName || '').trim();
  return [thickness, productName].filter(Boolean).join('|');
};

/** 문서 이력 폴더명 아래. 속성 팝업을 열지 않고 설정된 값을 본다. */
const FolderSpecSummary = ({ folder, folders }) => {
  const spec = folder?.spec;
  const generalId = folder?.rootKind === 'GENERAL' ? spec?.g04GeneralDocuId : '';
  const certId = folder?.rootKind === 'GENERAL' ? '' : spec?.g04docuGCertificationId;
  const [generalName, setGeneralName] = useState('');
  const [certLabel, setCertLabel] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!generalId) {
      setGeneralName('');
    } else {
      loadGeneralDocName(generalId).then((name) => {
        if (!cancelled) setGeneralName(name);
      });
    }
    return () => { cancelled = true; };
  }, [generalId]);

  useEffect(() => {
    let cancelled = false;
    if (!certId) {
      setCertLabel('');
    } else {
      loadCertLabel(certId).then((label) => {
        if (!cancelled) setCertLabel(label);
      });
    }
    return () => { cancelled = true; };
  }, [certId]);

  if (!subMatFolderActions.folderHasAttribute(folder)) return null;

  const bits = [];
  if (folder.rootKind === 'GENERAL') {
    if (generalName) bits.push(generalName);
  } else if (certLabel) {
    bits.push(certLabel);
  }
  if (folder.rootKind === 'TEST' && spec) {
    if (spec.productName) {
      bits.push(spec.productName);
    } else if (isProcessSpec(spec)) {
      const code = formatProductCode(spec.code || spec.codeByStandard).trim();
      if (code) bits.push(code);
    } else if (subMatFolderActions.isReportSpecFolder(folder)) {
      const code = formatProductCode(reportLookupCode(folder, folders)).trim();
      if (code) bits.push(code);
    } else if (spec.thickness != null && String(spec.thickness).trim() !== '') {
      bits.push(`두께 ${spec.thickness}mm`);
    }
    const grade = joinClassificationParts(spec.classificationParts);
    if (grade) bits.push(grade);
    else if (subMatFolderActions.isReportSpecFolder(folder) && !String(spec.thickness || '').trim()) {
      bits.push('이 제품 공통');
    }
  }
  const text = bits.join(' · ');
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      noWrap
      title={text || undefined}
      sx={{ display: 'block', mt: 0.25, minHeight: '1.25rem' }}
    >
      {text}
    </Typography>
  );
};

const SubMatFolderPropertyPanel = ({
  folder,
  folders,
  files,
  filesFolderId,
  onSelectFile,
  onSetCurrentFile,
  onContextMenu,
  onPreviewFile,
  onError,
}) => {
  const muiTheme = useTheme();
  const compact = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [settingId, setSettingId] = useState(null);
  const [helpAnchor, setHelpAnchor] = useState(null);
  const [sortKey, setSortKey] = useState('version');
  const [sortDir, setSortDir] = useState('desc');
  const { widths, startResize } = useColumnWidths(HISTORY_COL_DEFAULT, HISTORY_COL_MIN);
  const readOnly = Boolean(folder?.linkView);
  const uploadable = subMatFolderActions.isUploadableFolder(folder);
  const showHistory = uploadable || readOnly;
  const filesKey = folder?.linkView ? folder.sourceFolderId : folder?.id;
  const folderFiles = filesFolderId === filesKey ? files : [];

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(HISTORY_SORT_FIRST_DIR[key] || 'asc');
    }
  };

  useEffect(() => {
    setSortKey('version');
    setSortDir('desc');
  }, [folder?.id]);

  const sortedFiles = useMemo(
    () => [...folderFiles].sort((a, b) => compareHistoryFiles(a, b, sortKey, sortDir)),
    [folderFiles, sortKey, sortDir],
  );

  const currentFile = useMemo(
    () => folderFiles.find((f) => f.isCurrent) || null,
    [folderFiles],
  );

  const currentFileId = currentFile?.id || '';

  const handleSetCurrent = async (e, file) => {
    e.stopPropagation();
    if (!file || file.isCurrent || settingId) return;
    setSettingId(file.id);
    try {
      await onSetCurrentFile?.(file);
      onSelectFile?.(file.id);
    } finally {
      setSettingId(null);
    }
  };

  if (!folder) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">폴더를 선택하세요.</Typography>
      </Box>
    );
  }

  if (!showHistory) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>문서 이력</Typography>
        <Typography variant="body2" color="text.secondary">
          시스템 루트에는 문서 이력이 없습니다. 하위 폴더를 선택하세요.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 1 }}>
        <Typography variant="subtitle1">문서 이력</Typography>
        <IconButton
          size="small"
          aria-label="버전 선택 도움말"
          onClick={(e) => setHelpAnchor(e.currentTarget)}
          sx={{ p: 0.25 }}
        >
          <HelpOutlineIcon fontSize="small" color="action" />
        </IconButton>
        <Popover
          open={Boolean(helpAnchor)}
          anchorEl={helpAnchor}
          onClose={() => setHelpAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, maxWidth: 300 }}>
            <Typography variant="body2" gutterBottom>
              · 체크된 문서가 템플릿으로 모아 발송할 때 실제로 쓰이는 파일입니다.
            </Typography>
            <Typography variant="body2" gutterBottom>
              · 발송에 쓸 버전을 고르려면 해당 행의 체크를 누르세요. (폴더당 하나만)
            </Typography>
            <Typography variant="body2">
              · 컬럼 헤더(버전·이름·수정한 날짜·만료일·상태·크기)를 누르면 정렬됩니다.
            </Typography>
          </Box>
        </Popover>
      </Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.75,
          minWidth: 0,
        }}
      >
        <Box component="span" sx={{ flexShrink: 0, lineHeight: 1.45, fontSize: '1.05em', mt: '1px' }}>
          <SubMatFolderIcon folder={{
            ...folder,
            currentFile: folder.currentFile || currentFile,
          }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontSize: '0.9375rem', lineHeight: 1.45, color: 'text.primary' }}
          >
            {folder.name}
          </Typography>
          <FolderSpecSummary folder={folder} folders={folders} />
        </Box>
      </Box>

      {sortedFiles.length > 0 ? (
        <>
          {!currentFile && !readOnly && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }}>
              사용중 문서가 없습니다. 버전을 선택하세요.
            </Typography>
          )}
          {compact ? (
            <Box>
              {sortedFiles.map((f) => {
                const isComparison = f.fileRole === 'TEST_COMPARISON';
                return (
                  <Box
                    key={f.id}
                    onClick={() => onSelectFile?.(f.id)}
                    onContextMenu={(e) => onContextMenu?.(e, { kind: 'file', folder, file: f })}
                    {...bindLongPress((e) => onContextMenu?.(e, { kind: 'file', folder, file: f }))}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minHeight: 44,
                      borderBottom: 1,
                      borderColor: 'divider',
                      userSelect: 'none',
                      WebkitTouchCallout: 'none',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ width: 28, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {f.versionNo ?? ''}
                    </Typography>
                    <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }} title={f.fileName}>
                      {f.fileName}
                    </Typography>
                    {!isComparison && (
                      <Checkbox
                        size="small"
                        color="success"
                        checked={f.id === currentFileId}
                        disabled={readOnly || Boolean(settingId)}
                        icon={<CheckEmptyIcon />}
                        checkedIcon={<CheckMarkIcon />}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) handleSetCurrent(e, f);
                        }}
                        inputProps={{ 'aria-label': `${f.fileName} 사용중으로 설정` }}
                        sx={{ p: 0.25, flexShrink: 0 }}
                      />
                    )}
                    {f.comparisonFile?.documentPath && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await onPreviewFile?.(f.comparisonFile);
                          } catch (err) {
                            onError?.(err.message || '시험성과대비표를 열 수 없습니다.');
                          }
                        }}
                        sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.75rem', flexShrink: 0 }}
                      >
                        대비표
                      </Button>
                    )}
                    {f.documentPath && (
                      <IconButton
                        size="small"
                        aria-label="문서 보기"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await onPreviewFile?.(f);
                          } catch (err) {
                            onError?.(err.message || '파일을 열 수 없습니다.');
                          }
                        }}
                        sx={{ p: 0.5, flexShrink: 0 }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <HistoryHeader
              widths={widths}
              startResize={startResize}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
            />
            {sortedFiles.map((f) => (
              <HistoryRow
                key={f.id}
                widths={widths}
                file={f}
                folder={folder}
                current={f.id === currentFileId}
                checkDisabled={readOnly || Boolean(settingId)}
                onSelect={onSelectFile}
                onToggleCurrent={handleSetCurrent}
                onContextMenu={onContextMenu}
                onPreviewFile={onPreviewFile}
                onError={onError}
              />
            ))}
          </Box>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          업로드된 문서가 없습니다. 중간 탐색기에서 PDF를 추가하세요.
        </Typography>
      )}
    </Box>
  );
};

export default SubMatFolderPropertyPanel;
